import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, CardActionArea, Chip, TextField,
  InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions, Button,
  List, ListItem, ListItemText, Divider, Stack, ToggleButton, ToggleButtonGroup,
  Alert, Paper, Checkbox,
} from '@mui/material';
import {
  Search as SearchIcon, TableRestaurant as TableIcon, PointOfSale as PayIcon,
  Print as PrintIcon, Payments as CashIcon, CreditCard as CardIcon,
  PhoneAndroid as YapeIcon, LockOpen as OpenIcon, Lock as CloseIcon,
  SwapVert as MoveIcon, Add as AddIcon, Close as CloseSmallIcon,
  CallSplit as SplitIcon, SwapHoriz as SwapIcon, Receipt as ExpenseIcon,
} from '@mui/icons-material';
import { Calculate as CalcIcon, ReceiptLong as ReprintIcon } from '@mui/icons-material';
import { ordersApi } from '../api/orders.api';
import { cashApi } from '../api/cash.api';
import { sunatApi } from '../api/sunat.api';
import { tablesApi, TableZone, TABLE_ZONES } from '../api/tables.api';
import { customersApi } from '../api/customers.api';
import { getSocket } from '../api/socket';
import { useTiposDocumento } from '../hooks/useTiposDocumento';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { printCierreTicket } from '../utils/cashReceipt';
import { getIgvLabel } from '../config/igv';
import DenominationCalculator from '../components/cash/DenominationCalculator';
import { CashMovement, CashSession, ExchangeRate } from '../types/cash';
import { TICKET_CSS } from '../utils/ticketStyles';

const money = (n: any) => `S/ ${Number(n || 0).toFixed(2)}`;
const dollars = (n: any) => `US$ ${Number(n || 0).toFixed(2)}`;

const PAYMENT_METHODS = [
  { key: 'efectivo', label: 'Efectivo', icon: <CashIcon fontSize="small" /> },
  { key: 'efectivo_usd', label: 'Dolares', icon: <CashIcon fontSize="small" /> },
  { key: 'visa', label: 'Visa', icon: <CardIcon fontSize="small" /> },
  { key: 'mastercard', label: 'Mastercard', icon: <CardIcon fontSize="small" /> },
  { key: 'yape', label: 'Yape', icon: <YapeIcon fontSize="small" /> },
  { key: 'plin', label: 'Plin', icon: <YapeIcon fontSize="small" /> },
  { key: 'transferencia', label: 'Transfer.', icon: <CardIcon fontSize="small" /> },
  { key: 'credito', label: 'Credito', icon: <CardIcon fontSize="small" /> },
];

const EMPRESA = { nombre: 'REST CAMPESTRE LA OLLA DE JUANITA EIRL', ruc: '20223351205' };

const Caja: React.FC = () => {
  const [cash, setCash] = useState<any>(null); // respuesta de /cash/current
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [zoneFilter, setZoneFilter] = useState<TableZone | 'todas'>('todas');

  // Apertura
  const [openingAmount, setOpeningAmount] = useState('');
  const [openingAmountUsd, setOpeningAmountUsd] = useState('');
  const [exchangeRateInput, setExchangeRateInput] = useState('');
  const [exchangeRate, setExchangeRate] = useState<ExchangeRate | null>(null);
  const [opening, setOpening] = useState(false);

  // Cobro
  const [selected, setSelected] = useState<any>(null);
  const [method, setMethod] = useState('efectivo');
  // RF-06: tipo de comprobante. 'auto' deja que el backend decida segun el doc.
  // del cliente (RUC=factura, otro=boleta). 'nota_venta' y 'doc_interno' NO van
  // a SUNAT — se imprime ticket marcado y se numera localmente.
  const [comprobanteType, setComprobanteType] = useState<
    'auto' | 'boleta' | 'factura' | 'nota_venta' | 'doc_interno'
  >('auto');
  // RF-05: si esta activo, el comprobante SUNAT muestra una sola linea
  // "POR CONSUMO" en vez del detalle. Inventario igual.
  const [porConsumo, setPorConsumo] = useState(false);
  const [received, setReceived] = useState('');
  // Monto recibido en dolares (cuando method='efectivo_usd'). Se convierte a
  // PEN con el TC de la sesion para registrar la venta y calcular el cambio.
  const [receivedUsd, setReceivedUsd] = useState('');
  const [docType, setDocType] = useState('');
  const [docNumber, setDocNumber] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [paying, setPaying] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);
  const [lookupMsg, setLookupMsg] = useState<{ kind: 'ok' | 'error' | 'info'; text: string } | null>(null);

  // Lineas de pago para soportar pagos mixtos: ej. Visa S/30 + Yape S/14 = total S/44.
  // Cuando hay >= 1 linea, el toggle de metodo arriba se ignora y se envia payments[].
  // Para credito (RF-08), la linea ademas lleva customerId y dueDate.
  const [paymentLines, setPaymentLines] = useState<
    Array<{
      method: string;
      amount: number;
      reference?: string;
      customerId?: number;
      dueDate?: string;
      customerName?: string;
    }>
  >([]);
  const [lineAmount, setLineAmount] = useState('');
  const [lineRef, setLineRef] = useState('');

  // RF-08: para lineas de pago con method='credito' se exige cliente + fecha
  // programada de pago. Mantenemos esto en state aparte del lineAmount/lineRef
  // para no contaminar el flujo de pagos comunes.
  const [lineCustomerId, setLineCustomerId] = useState<number | ''>('');
  const [lineDueDate, setLineDueDate] = useState('');
  const [customers, setCustomers] = useState<any[]>([]);

  const { tipos: tiposDocumento } = useTiposDocumento();
  const docTypes = [
    { key: '', label: 'Sin doc', max: 15 },
    ...tiposDocumento.map((t) => ({ key: t.abreviatura, label: t.nombre, max: t.maxLength })),
  ];

  // RF-04: dividir cuenta por items. selectedItemIds son los items a mover a un
  // nuevo pedido. El pedido original queda con los items restantes.
  const [splitOpen, setSplitOpen] = useState(false);
  const [splitting, setSplitting] = useState(false);
  const [selectedItemIds, setSelectedItemIds] = useState<Set<number>>(new Set());
  // RF-04b: modo del split. 'items' = por seleccion; 'equal' = N partes iguales.
  const [splitMode, setSplitMode] = useState<'items' | 'equal'>('items');
  const [splitParts, setSplitParts] = useState(2);

  // RF-03: traspaso de item a otra mesa con autorizacion admin.
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [transferItem, setTransferItem] = useState<any>(null);
  const [transferTargetTable, setTransferTargetTable] = useState<number | ''>('');
  const [transferAdminEmail, setTransferAdminEmail] = useState('');
  const [transferAdminPassword, setTransferAdminPassword] = useState('');
  const [transferReason, setTransferReason] = useState('');
  const [allTables, setAllTables] = useState<any[]>([]);

  // Movimiento
  const [movOpen, setMovOpen] = useState(false);
  const [movType, setMovType] = useState<'ingreso' | 'egreso'>('egreso');
  const [movAmount, setMovAmount] = useState('');
  const [movConcept, setMovConcept] = useState('');

  // Gastos diarios (egresos de la sesion abierta).
  const [expenses, setExpenses] = useState<CashMovement[]>([]);
  const [expensesTotal, setExpensesTotal] = useState(0);
  const [expenseOpen, setExpenseOpen] = useState(false);
  const [expenseAmount, setExpenseAmount] = useState('');
  const [expenseConcept, setExpenseConcept] = useState('');
  const [savingExpense, setSavingExpense] = useState(false);

  // Cierre
  const [closeOpen, setCloseOpen] = useState(false);
  const [counted, setCounted] = useState('');
  const [countedUsd, setCountedUsd] = useState('');
  const [closeNotes, setCloseNotes] = useState('');
  const [closing, setClosing] = useState(false);
  const [calcOpen, setCalcOpen] = useState(false);
  const [lastClose, setLastClose] = useState<CashSession | null>(null);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(async () => {
    try {
      const cashRes = await cashApi.current();
      setCash(cashRes.data);
      if (cashRes.data?.open) {
        const [ordRes, expRes] = await Promise.all([
          ordersApi.getActive().catch(() => null),
          cashApi.getExpenses().catch(() => null),
        ]);
        setOrders(ordRes?.data || []);
        if (expRes?.data) {
          setExpenses(expRes.data.expenses || []);
          setExpensesTotal(Number(expRes.data.total) || 0);
        } else {
          // Fallback: /cash/current ya devuelve los movimientos de la sesion.
          const fallback = (cashRes.data.movements || []).filter(
            (m: CashMovement) => m.type === 'egreso',
          );
          setExpenses(fallback);
          setExpensesTotal(
            fallback.reduce((acc, m) => acc + Number(m.amount || 0), 0),
          );
        }
      } else {
        setOrders([]);
        setExpenses([]);
        setExpensesTotal(0);
      }
    } catch (err) {
      console.error('Error cargando caja:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Trae el tipo de cambio del dia desde SUNAT cacheado en backend. Solo lo necesita
  // la pantalla de apertura, pero lo pedimos al montar para autocompletar el campo.
  const loadExchangeRate = useCallback(async (force = false) => {
    try {
      const res = force
        ? await cashApi.refreshExchangeRate()
        : await cashApi.getExchangeRate();
      setExchangeRate(res.data);
      setExchangeRateInput(String(res.data.sell));
    } catch (err) {
      console.error('Error TC SUNAT:', err);
    }
  }, []);

  useEffect(() => {
    load();
    loadExchangeRate();
    // Cargar todas las mesas una vez para el selector de traspaso (RF-03).
    tablesApi.getAll().then((r) => setAllTables(r.data || [])).catch(() => {});
    // Cargar clientes para el selector de credito (RF-08).
    customersApi.getAll().then((r) => setCustomers(r.data || [])).catch(() => {});
    const socket = getSocket();
    if (!socket.connected) socket.connect();
    const handler = () => load();
    socket.on('orders:changed', handler);
    pollRef.current = setInterval(load, 10000);
    return () => {
      socket.off('orders:changed', handler);
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [load, loadExchangeRate]);

  const abrirCaja = async () => {
    const amt = parseFloat(openingAmount);
    if (isNaN(amt) || amt < 0) { alert('Ingresa un monto inicial valido en soles.'); return; }
    const amtUsd = openingAmountUsd ? parseFloat(openingAmountUsd) : 0;
    if (isNaN(amtUsd) || amtUsd < 0) { alert('Monto USD invalido.'); return; }
    const tc = exchangeRateInput ? parseFloat(exchangeRateInput) : 0;
    if (isNaN(tc) || tc < 0) { alert('Tipo de cambio invalido.'); return; }
    setOpening(true);
    try {
      await cashApi.open({
        openingAmount: amt,
        openingAmountUsd: amtUsd,
        exchangeRate: tc,
      });
      setOpeningAmount('');
      setOpeningAmountUsd('');
      load();
    } catch (err: any) {
      alert(err?.response?.data?.message || 'No se pudo abrir la caja.');
    } finally {
      setOpening(false);
    }
  };

  const registrarMovimiento = async () => {
    const amt = parseFloat(movAmount);
    if (isNaN(amt) || amt <= 0) { alert('Monto invalido.'); return; }
    if (!movConcept.trim()) { alert('Indica el concepto.'); return; }
    try {
      await cashApi.movement(movType, amt, movConcept.trim());
      setMovOpen(false); setMovAmount(''); setMovConcept('');
      load();
    } catch (err: any) {
      alert(err?.response?.data?.message || 'No se pudo registrar el movimiento.');
    }
  };

  const registrarGasto = async () => {
    const amt = parseFloat(expenseAmount);
    if (isNaN(amt) || amt <= 0) { alert('Ingresa un monto de gasto mayor a 0.'); return; }
    if (!expenseConcept.trim()) { alert('Indica el concepto del gasto.'); return; }
    setSavingExpense(true);
    try {
      await cashApi.addExpense(amt, expenseConcept.trim());
      setExpenseOpen(false);
      setExpenseAmount('');
      setExpenseConcept('');
      await load();
    } catch (err: any) {
      alert(err?.response?.data?.message || 'No se pudo registrar el gasto.');
    } finally {
      setSavingExpense(false);
    }
  };

  // Reimprime un cierre con el detalle de gastos. Si el detalle no esta
  // disponible, imprime el snapshot de la sesion (que ya trae totalEgresos).
  const reimprimirCierre = async (session: CashSession) => {
    try {
      const res = await cashApi.getById(session.id);
      printCierreTicket(res.data.session, res.data.movements);
    } catch (err) {
      console.error('No se pudo cargar el detalle del cierre:', err);
      printCierreTicket(session);
    }
  };

  const openOrder = async (order: any) => {
    setMethod('efectivo'); setReceived(''); setReceivedUsd(''); setDocType(''); setDocNumber(''); setCustomerName('');
    setLookupMsg(null);
    setPaymentLines([]); setLineAmount(''); setLineRef('');
    setComprobanteType('auto');
    setPorConsumo(false);
    try {
      const res = await ordersApi.getById(order.id);
      setSelected(res.data);
    } catch {
      setSelected(order);
    }
  };

  // Consulta DNI/RUC contra el backend (apis.net.pe -> scraper fallback) y autocompleta
  // el nombre del cliente. Pensado para la facturacion electronica del spec.
  const buscarDocumento = async () => {
    if (!docType || !docNumber) {
      setLookupMsg({ kind: 'info', text: 'Selecciona tipo y numero de documento.' });
      return;
    }
    const trimmed = docNumber.trim();
    if (docType === 'DNI' && trimmed.length !== 8) {
      setLookupMsg({ kind: 'error', text: 'El DNI debe tener 8 digitos.' });
      return;
    }
    if (docType === 'RUC' && trimmed.length !== 11) {
      setLookupMsg({ kind: 'error', text: 'El RUC debe tener 11 digitos.' });
      return;
    }
    if (docType !== 'DNI' && docType !== 'RUC') {
      setLookupMsg({ kind: 'info', text: 'Solo se puede buscar DNI o RUC automaticamente.' });
      return;
    }
    setLookingUp(true);
    setLookupMsg(null);
    try {
      const res =
        docType === 'DNI'
          ? await sunatApi.consultarDni(trimmed)
          : await sunatApi.consultarRuc(trimmed);
      const data = res.data;
      if (data?.success && data.fullName) {
        setCustomerName(data.fullName);
        setLookupMsg({
          kind: 'ok',
          text: `${data.fullName}${data.taxCondition ? ` · ${data.taxCondition}` : ''}`,
        });
      } else {
        setLookupMsg({ kind: 'error', text: data?.message || 'No se encontro el documento.' });
      }
    } catch (err: any) {
      setLookupMsg({
        kind: 'error',
        text: err?.response?.data?.message || 'Error consultando el documento.',
      });
    } finally {
      setLookingUp(false);
    }
  };

  const total = Number(selected?.total ?? 0);
  const receivedNum = parseFloat(received);
  const change = method === 'efectivo' && !isNaN(receivedNum) ? Math.max(0, receivedNum - total) : 0;
  const cashError = method === 'efectivo' && received !== '' && (isNaN(receivedNum) || receivedNum < total);

  // Cobro en dolares: convertir recibido USD a PEN con el TC de la sesion.
  const tcSession = Number(cash?.session?.exchangeRate || 0);
  const receivedUsdNum = parseFloat(receivedUsd);
  const receivedUsdInPen = !isNaN(receivedUsdNum) && tcSession > 0 ? receivedUsdNum * tcSession : 0;
  const changeUsdInPen = Math.max(0, receivedUsdInPen - total);
  // Cambio en USD para que el cajero devuelva la moneda dura si quiere.
  const changeUsd = tcSession > 0 ? changeUsdInPen / tcSession : 0;
  const usdError =
    method === 'efectivo_usd' &&
    receivedUsd !== '' &&
    (isNaN(receivedUsdNum) || receivedUsdInPen < total);

  // Resumen de pagos mixtos.
  const paidSum = Math.round(paymentLines.reduce((s, l) => s + l.amount, 0) * 100) / 100;
  const remaining = Math.round((total - paidSum) * 100) / 100;
  const mixtoListo = paymentLines.length > 0 && Math.abs(remaining) < 0.01;

  // Agrega una linea con el metodo seleccionado en el toggle + monto. Si no se
  // ingresa monto, asume "lo que falta" (atajo para split rapido). Para credito
  // (RF-08) ademas exige cliente y fecha programada.
  const addPaymentLine = () => {
    const amt = lineAmount ? parseFloat(lineAmount) : remaining;
    if (isNaN(amt) || amt <= 0) {
      alert('Monto invalido para la linea de pago.');
      return;
    }
    if (amt > remaining + 0.01) {
      alert(`Excede lo que falta cobrar (S/ ${remaining.toFixed(2)}).`);
      return;
    }
    if (method === 'credito') {
      if (!lineCustomerId) {
        alert('Para credito tienes que seleccionar un cliente.');
        return;
      }
      if (!lineDueDate) {
        alert('Indica la fecha programada de pago.');
        return;
      }
    }
    const customer = customers.find((c) => c.id === lineCustomerId);
    setPaymentLines([
      ...paymentLines,
      {
        method,
        amount: amt,
        reference: lineRef || undefined,
        customerId: method === 'credito' ? Number(lineCustomerId) : undefined,
        dueDate: method === 'credito' ? lineDueDate : undefined,
        customerName: method === 'credito' ? customer?.name : undefined,
      },
    ]);
    setLineAmount('');
    setLineRef('');
    setLineCustomerId('');
    setLineDueDate('');
  };

  const removePaymentLine = (idx: number) => {
    setPaymentLines(paymentLines.filter((_, i) => i !== idx));
  };

  // Abre el dialogo de split. Si el usuario confirma, los items marcados se
  // mueven a un nuevo pedido en la misma mesa; ambos quedan cobrables aparte.
  const openSplitDialog = () => {
    setSelectedItemIds(new Set());
    setSplitMode('items');
    setSplitParts(2);
    setSplitOpen(true);
  };

  // RF-04b: confirma split en partes iguales. Crea N pedidos hijos y deja el
  // original cancelado con nota.
  const confirmSplitEqual = async () => {
    if (!selected) return;
    if (splitParts < 2 || splitParts > 10) {
      alert('Las partes deben estar entre 2 y 10.');
      return;
    }
    setSplitting(true);
    try {
      await ordersApi.splitEqual(selected.id, splitParts);
      setSplitOpen(false);
      setSelected(null);
      load();
    } catch (err: any) {
      alert(err?.response?.data?.message || 'No se pudo dividir en partes iguales.');
    } finally {
      setSplitting(false);
    }
  };

  const toggleSplitItem = (itemId: number) => {
    const next = new Set(selectedItemIds);
    if (next.has(itemId)) next.delete(itemId);
    else next.add(itemId);
    setSelectedItemIds(next);
  };

  const confirmSplit = async () => {
    if (!selected) return;
    if (selectedItemIds.size === 0) {
      alert('Selecciona al menos un item para separar.');
      return;
    }
    const totalItems = (selected.items || []).filter(
      (it: any) => it.status !== 'cancelado',
    ).length;
    if (selectedItemIds.size === totalItems) {
      alert('No puedes separar TODOS los items: el pedido original quedaria vacio.');
      return;
    }
    setSplitting(true);
    try {
      await ordersApi.split(selected.id, Array.from(selectedItemIds));
      setSplitOpen(false);
      setSelected(null); // se cerro el modal de cobro; el cajero ve la lista
      load();
    } catch (err: any) {
      alert(err?.response?.data?.message || 'No se pudo dividir la cuenta.');
    } finally {
      setSplitting(false);
    }
  };

  const splitItemsTotal = (selected?.items || [])
    .filter((it: any) => selectedItemIds.has(it.id))
    .reduce((s: number, it: any) => s + Number(it.subtotal), 0);

  // RF-03: abre el dialogo de traspaso para un item especifico.
  const openTransferDialog = (item: any) => {
    setTransferItem(item);
    setTransferTargetTable('');
    setTransferAdminEmail('');
    setTransferAdminPassword('');
    setTransferReason('');
    setTransferOpen(true);
  };

  const confirmTransfer = async () => {
    if (!selected || !transferItem) return;
    if (!transferTargetTable) {
      alert('Selecciona la mesa destino.');
      return;
    }
    // El spec exige autorizacion explicita del Admin, aun si el usuario actual
    // ya es admin. Pedimos password siempre.
    if (!transferAdminEmail || !transferAdminPassword) {
      alert('Email y contrasena de Admin son obligatorios.');
      return;
    }
    setTransferring(true);
    try {
      await ordersApi.transferItem(selected.id, {
        itemId: transferItem.id,
        targetTableId: Number(transferTargetTable),
        adminEmail: transferAdminEmail,
        adminPassword: transferAdminPassword,
        reason: transferReason || undefined,
      });
      setTransferOpen(false);
      setSelected(null); // refresca la lista
      load();
    } catch (err: any) {
      alert(err?.response?.data?.message || 'No se pudo trasladar el item.');
    } finally {
      setTransferring(false);
    }
  };

  const imprimirTicket = (order: any) => {
    const fecha = new Date().toLocaleString('es-PE');
    const metodoLabel = PAYMENT_METHODS.find((m) => m.key === method)?.label || method;
    const items = (order.items || [])
      .map((it: any) => `<tr><td>${it.quantity}x ${it.recipe?.name ?? ''}</td><td class="right">${money(it.subtotal)}</td></tr>`)
      .join('');
    const html = `<html><head><style>${TICKET_CSS}</style></head><body>
      <div class="center bold" style="font-size:15px;">${EMPRESA.nombre}</div>
      <div class="center">RUC: ${EMPRESA.ruc}</div>
      <div class="center" style="margin-top:4px;">TICKET DE VENTA</div>
      <div class="line"></div>
      <div>Pedido: ${order.code}</div>
      <div>Mesa: ${order.table?.name || 'Mesa ' + order.tableId}</div>
      <div>Fecha: ${fecha}</div>
      ${order.customerDocument ? `<div>${order.customerDocType || 'Doc'}: ${order.customerDocument}</div>` : ''}
      ${order.customerName ? `<div>Cliente: ${order.customerName}</div>` : ''}
      <div>Metodo: ${metodoLabel}</div>
      <div class="line"></div>
      <table>${items}</table>
      <div class="line"></div>
      <table>
        <tr><td>Subtotal:</td><td class="right">${money(order.subtotal)}</td></tr>
        <tr><td>${getIgvLabel()}:</td><td class="right">${money(order.tax)}</td></tr>
        <tr class="bold"><td style="font-size:17px;">TOTAL:</td><td class="right" style="font-size:17px;">${money(order.total)}</td></tr>
      </table>
      ${method === 'efectivo' && received ? `<div class="line"></div><table>
        <tr><td>Recibido:</td><td class="right">${money(receivedNum)}</td></tr>
        <tr class="bold"><td>Cambio:</td><td class="right">${money(change)}</td></tr>
      </table>` : ''}
      <div class="line"></div>
      <div class="center" style="margin-top:6px;">Gracias por su preferencia</div>
      <script>window.onload=function(){window.print();}</script>
    </body></html>`;
    const w = window.open('', '_blank', 'width=320,height=600');
    if (w) { w.document.write(html); w.document.close(); }
  };

  const cobrar = async () => {
    if (!selected) return;
    // Modo mixto: deben sumar exactamente el total.
    if (paymentLines.length > 0 && !mixtoListo) {
      alert(`Faltan S/ ${remaining.toFixed(2)} por cobrar.`);
      return;
    }
    // Modo simple efectivo: validar recibido >= total.
    if (
      paymentLines.length === 0 &&
      method === 'efectivo' &&
      (received === '' || isNaN(receivedNum) || receivedNum < total)
    ) {
      alert('El monto recibido debe ser mayor o igual al total.');
      return;
    }
    // Modo simple efectivo USD: validar TC + monto USD convertido >= total PEN.
    if (
      paymentLines.length === 0 &&
      method === 'efectivo_usd'
    ) {
      if (tcSession <= 0) {
        alert('No hay tipo de cambio cargado en este turno. No se puede cobrar en dolares.');
        return;
      }
      if (receivedUsd === '' || isNaN(receivedUsdNum) || receivedUsdInPen < total) {
        alert('El monto USD recibido (convertido a PEN) debe ser mayor o igual al total.');
        return;
      }
    }
    setPaying(true);
    try {
      const data: any = {};
      if (customerName) data.customerName = customerName;
      if (docType && docNumber) { data.customerDocType = docType; data.customerDocument = docNumber; }
      if (comprobanteType !== 'auto') data.comprobanteType = comprobanteType;
      if (porConsumo) data.porConsumo = true;
      if (paymentLines.length > 0) {
        // Mixto: backend valida que la suma sea igual al total. Para 'credito'
        // van tambien customerId y dueDate (RF-08).
        data.payments = paymentLines.map((l) => ({
          method: l.method,
          amount: l.amount,
          reference: l.reference,
          currency: l.method === 'efectivo_usd' ? 'USD' : 'PEN',
          customerId: l.customerId,
          dueDate: l.dueDate,
        }));
      } else {
        data.paymentMethod = method;
      }
      await ordersApi.pagar(selected.id, data);
      imprimirTicket({ ...selected, customerName, customerDocType: docType, customerDocument: docNumber });
      setSelected(null);
      load();
    } catch (err: any) {
      console.error('Error al cobrar:', err);
      alert(err?.response?.data?.message || 'No se pudo cobrar el pedido.');
    } finally {
      setPaying(false);
    }
  };

  // Descuadre en vivo del modal de cierre
  const expected = Number(cash?.expectedCash ?? 0);
  const countedNum = parseFloat(counted);
  const diff = !isNaN(countedNum) ? Math.round((countedNum - expected) * 100) / 100 : 0;
  const hasDescuadre = !isNaN(countedNum) && Math.abs(diff) >= 0.01;
  const notesRequired = hasDescuadre; // observacion obligatoria si hay descuadre
  const closeBlocked = isNaN(countedNum) || countedNum < 0 || (notesRequired && !closeNotes.trim());

  const cerrarCaja = async () => {
    if (isNaN(countedNum) || countedNum < 0) { alert('Ingresa el efectivo contado en soles.'); return; }
    const countedUsdNum = countedUsd ? parseFloat(countedUsd) : 0;
    if (isNaN(countedUsdNum) || countedUsdNum < 0) { alert('Efectivo USD invalido.'); return; }
    if (notesRequired && !closeNotes.trim()) {
      alert('Hay un descuadre: la observacion es obligatoria para cerrar la caja.');
      return;
    }
    setClosing(true);
    try {
      const res = await cashApi.close({
        countedCash: countedNum,
        countedCashUsd: countedUsdNum,
        notes: closeNotes.trim() || undefined,
      });
      setLastClose(res.data);
      try {
        // El detalle se relee del backend para no imprimir una lista desactualizada.
        const detail = await cashApi.getById(res.data.id);
        printCierreTicket(detail.data.session, detail.data.movements);
      } catch {
        printCierreTicket(res.data, expenses);
      }
      setCloseOpen(false); setCounted(''); setCountedUsd(''); setCloseNotes('');
      load();
    } catch (err: any) {
      alert(err?.response?.data?.message || 'No se pudo cerrar la caja.');
    } finally {
      setClosing(false);
    }
  };

  const q = search.trim().toLowerCase();
  const byText = q
    ? orders.filter((o) =>
        (o.code || '').toLowerCase().includes(q) ||
        String(o.table?.number ?? o.tableId).includes(q) ||
        (o.table?.name || '').toLowerCase().includes(q))
    : orders;
  const filtered = zoneFilter === 'todas'
    ? byText
    : byText.filter((o) => o.table?.zone === zoneFilter);

  const maxLen = docTypes.find((d) => d.key === docType)?.max || 15;

  if (loading && !cash) return <LoadingSpinner />;

  // ====== CAJA CERRADA: pantalla de apertura ======
  if (!cash?.open) {
    return (
      <Box sx={{ maxWidth: 460, mx: 'auto', mt: 6 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <OpenIcon sx={{ fontSize: 48, color: '#D97706' }} />
          <Typography variant="h5" sx={{ mt: 1, mb: 0.5 }}>Caja cerrada</Typography>
          <Typography variant="body2" sx={{ color: '#6B7280', mb: 3 }}>
            Define el fondo inicial en soles y dolares, confirma el tipo de cambio y abre el turno.
          </Typography>
          <TextField
            fullWidth label="Fondo inicial (PEN)" type="number" value={openingAmount}
            onChange={(e) => setOpeningAmount(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start">S/</InputAdornment> }}
            sx={{ mb: 2 }}
            autoFocus
          />
          <TextField
            fullWidth label="Fondo inicial (USD) — opcional" type="number" value={openingAmountUsd}
            onChange={(e) => setOpeningAmountUsd(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start">US$</InputAdornment> }}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1 }}>
            <TextField
              fullWidth label="Tipo de cambio" type="number" value={exchangeRateInput}
              onChange={(e) => setExchangeRateInput(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start">S/ por US$</InputAdornment> }}
              helperText={
                exchangeRate
                  ? `SUNAT ${exchangeRate.date} · compra ${exchangeRate.buy} · venta ${exchangeRate.sell}${exchangeRate.cached ? ' (cache)' : ''}`
                  : 'Cargando TC del dia...'
              }
            />
            <Button
              variant="outlined" sx={{ mt: 0.5, whiteSpace: 'nowrap' }}
              onClick={() => loadExchangeRate(true)}
            >
              Traer TC
            </Button>
          </Box>
          <Button
            fullWidth variant="contained" size="large" startIcon={<OpenIcon />}
            disabled={opening} onClick={abrirCaja} sx={{ mt: 1 }}
          >
            {opening ? 'Abriendo...' : 'Abrir turno'}
          </Button>
          {lastClose && (
            <Button
              fullWidth variant="outlined" startIcon={<ReprintIcon />} sx={{ mt: 1.5 }}
              onClick={() => reimprimirCierre(lastClose)}
            >
              Reimprimir ultimo ticket de cierre
            </Button>
          )}
        </Paper>
      </Box>
    );
  }

  const s = cash.session;
  const sales = cash.sales || {};

  // ====== CAJA ABIERTA ======
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <PayIcon sx={{ fontSize: 32, color: '#D97706' }} />
        <Typography variant="h4">Caja</Typography>
        <Chip label="ABIERTA" color="success" size="small" />
        <Box sx={{ flexGrow: 1 }} />
        <Button
          variant="outlined" color="error" startIcon={<ExpenseIcon />}
          onClick={() => { setExpenseAmount(''); setExpenseConcept(''); setExpenseOpen(true); }}
        >
          Registrar gasto
        </Button>
        <Button variant="outlined" startIcon={<MoveIcon />} onClick={() => setMovOpen(true)}>Movimiento</Button>
        <Button variant="contained" color="error" startIcon={<CloseIcon />} onClick={() => { setCounted(''); setCloseOpen(true); }}>
          Cerrar caja
        </Button>
      </Box>

      {/* Resumen de la sesion */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        {[
          { l: 'Fondo inicial', v: `${money(s.openingAmount)} · ${dollars(s.openingAmountUsd)}` },
          { l: 'Ventas totales', v: money(sales.total) },
          {
            l: 'Efectivo esperado',
            v: `${money(cash.expectedCash)} · ${dollars(cash.expectedCashUsd)}`,
          },
          { l: 'Pedidos cobrados', v: sales.count ?? 0 },
        ].map((k) => (
          <Grid key={k.l} size={{ xs: 6, md: 3 }}>
            <Paper sx={{ p: 1.5 }}>
              <Typography variant="caption" sx={{ color: '#6B7280' }}>{k.l}</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>{k.v}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>
      <Typography variant="caption" sx={{ color: '#6B7280', display: 'block' }}>
        Efectivo {money(sales.efectivo)} · USD {dollars(sales.efectivoUsd)} · Visa {money(sales.visa)} · Mastercard {money(sales.mastercard)} · Yape {money(sales.yape)} · Plin {money(sales.plin)} · Transf. {money(sales.transferencia)} · Credito {money(sales.credito)}
      </Typography>
      <Typography variant="caption" sx={{ color: '#6B7280' }}>
        Abierta por {s.openedByName || '-'} · {new Date(s.openedAt).toLocaleString('es-PE')} · TC S/ {Number(s.exchangeRate || 0).toFixed(4)}
      </Typography>

      {/* Gastos de la sesion: lista, importes y total acumulado */}
      <Paper sx={{ p: 1.5, mt: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <ExpenseIcon sx={{ color: '#DC2626' }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Gastos de la sesión
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Chip
            size="small"
            color={expenses.length > 0 ? 'error' : 'default'}
            label={`Total: ${money(expensesTotal)}`}
          />
        </Box>
        {expenses.length === 0 ? (
          <Typography variant="body2" sx={{ color: '#6B7280' }}>
            Sin gastos registrados en este turno.
          </Typography>
        ) : (
          <List dense disablePadding sx={{ maxHeight: 240, overflow: 'auto' }}>
            {expenses.map((e) => (
              <ListItem
                key={e.id}
                disableGutters
                secondaryAction={
                  <Typography sx={{ fontWeight: 700 }}>{money(e.amount)}</Typography>
                }
              >
                <ListItemText
                  primary={e.concept}
                  secondary={`${new Date(e.createdAt).toLocaleString('es-PE')}${
                    e.createdByName ? ` · ${e.createdByName}` : ''
                  }`}
                />
              </ListItem>
            ))}
          </List>
        )}
      </Paper>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', my: 2 }}>
        <TextField
          placeholder="Buscar pedido por codigo o numero de mesa..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, minWidth: 240, maxWidth: 480 }}
          InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>) }}
        />
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          <Chip
            label="Todas las zonas" size="small"
            color={zoneFilter === 'todas' ? 'primary' : 'default'}
            onClick={() => setZoneFilter('todas')}
          />
          {TABLE_ZONES.map((z) => (
            <Chip
              key={z.key} label={z.label} size="small"
              color={zoneFilter === z.key ? 'primary' : 'default'}
              onClick={() => setZoneFilter(z.key)}
            />
          ))}
        </Box>
      </Box>

      {filtered.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6, color: '#9CA3AF' }}>
          <TableIcon sx={{ fontSize: 48 }} />
          <Typography variant="h6" sx={{ mt: 1 }}>Sin pedidos para cobrar</Typography>
          <Typography variant="body2">{q ? 'Ningun pedido coincide.' : 'No hay mesas con pedidos activos.'}</Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {filtered.map((o) => (
            <Grid key={o.id} size={{ xs: 12, sm: 6, md: 3 }}>
              <Card sx={{ borderTop: '4px solid #D97706' }}>
                <CardActionArea onClick={() => openOrder(o)}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <TableIcon sx={{ color: '#D97706' }} />
                      <Typography variant="h6" sx={{ fontWeight: 800 }}>
                        {o.table?.name || `Mesa ${o.table?.number ?? o.tableId}`}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: '#6B7280' }}>
                      {o.code}
                      {o.comensales ? ` · 👥 ${o.comensales}` : ''}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, mt: 1 }}>{money(o.total)}</Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Dialogo de cobro */}
      <Dialog open={!!selected} onClose={() => !paying && setSelected(null)} maxWidth="xs" fullWidth>
        {selected && (
          <>
            <DialogTitle>
              Cobrar — {selected.code}
              <Typography variant="body2" sx={{ color: '#6B7280' }}>
                {selected.table?.name || `Mesa ${selected.table?.number ?? selected.tableId}`}
                {selected.comensales ? ` · ${selected.comensales} ${selected.comensales === 1 ? 'persona' : 'personas'}` : ''}
              </Typography>
            </DialogTitle>
            <DialogContent dividers>
              <List dense>
                {(selected.items || []).map((it: any) => (
                  <ListItem
                    key={it.id}
                    disableGutters
                    secondaryAction={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Button
                          size="small" variant="text" color="warning"
                          startIcon={<SwapIcon />}
                          onClick={() => openTransferDialog(it)}
                          sx={{ minWidth: 0 }}
                        >
                          Trasladar
                        </Button>
                        <Typography>{money(it.subtotal)}</Typography>
                      </Stack>
                    }
                  >
                    <ListItemText
                      primary={`${it.quantity}x ${it.recipe?.name ?? ''}`}
                      secondary={it.notes || undefined}
                    />
                  </ListItem>
                ))}
              </List>
              <Divider sx={{ my: 1 }} />
              <Stack spacing={0.5}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><span>Subtotal</span><span>{money(selected.subtotal)}</span></Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><span>{getIgvLabel()}</span><span>{money(selected.tax)}</span></Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 18 }}><span>TOTAL</span><span>{money(selected.total)}</span></Box>
              </Stack>

              <Divider sx={{ my: 1.5 }} />
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                Metodo de pago
              </Typography>
              {/* Grilla 4 columnas para que cada metodo respire y no quede el toggle apretado */}
              <Grid container spacing={1}>
                {PAYMENT_METHODS.map((m) => {
                  const active = method === m.key;
                  return (
                    <Grid key={m.key} size={{ xs: 6, sm: 3 }}>
                      <Button
                        fullWidth
                        variant={active ? 'contained' : 'outlined'}
                        color={active ? 'primary' : 'inherit'}
                        onClick={() => setMethod(m.key)}
                        sx={{
                          flexDirection: 'column',
                          py: 1.2,
                          gap: 0.3,
                          textTransform: 'none',
                          fontWeight: 700,
                          fontSize: 12,
                          borderColor: active ? undefined : '#D1D5DB',
                        }}
                      >
                        {m.icon}
                        {m.label}
                      </Button>
                    </Grid>
                  );
                })}
              </Grid>

              {method === 'efectivo' && paymentLines.length === 0 && (
                <Box sx={{ mt: 1.5 }}>
                  <TextField
                    fullWidth size="small" label="Monto recibido" type="number"
                    value={received} onChange={(e) => setReceived(e.target.value)}
                    error={cashError}
                    InputProps={{ startAdornment: <InputAdornment position="start">S/</InputAdornment> }}
                  />
                  {received !== '' && !isNaN(receivedNum) && !cashError && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, fontWeight: 700 }}>
                      <span>Cambio</span><span style={{ color: '#10B981' }}>{money(change)}</span>
                    </Box>
                  )}
                  {cashError && <Alert severity="warning" sx={{ mt: 1, py: 0 }}>Debe ser mayor o igual al total.</Alert>}
                </Box>
              )}

              {/* Cobro en DOLARES: pide monto USD y convierte a PEN al TC del turno */}
              {method === 'efectivo_usd' && paymentLines.length === 0 && (
                <Box sx={{ mt: 1.5, p: 1.5, bgcolor: '#F0FDF4', borderRadius: 1, border: '1px solid #BBF7D0' }}>
                  {tcSession > 0 ? (
                    <>
                      <Typography variant="caption" sx={{ color: '#065F46', fontWeight: 700 }}>
                        TC del turno: S/ {tcSession.toFixed(4)} / US$
                      </Typography>
                      <TextField
                        fullWidth size="small" label="Monto USD recibido" type="number"
                        value={receivedUsd} onChange={(e) => setReceivedUsd(e.target.value)}
                        error={usdError}
                        InputProps={{ startAdornment: <InputAdornment position="start">US$</InputAdornment> }}
                        sx={{ mt: 1, bgcolor: '#FFFFFF' }}
                      />
                      {receivedUsd !== '' && !isNaN(receivedUsdNum) && (
                        <Stack spacing={0.3} sx={{ mt: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span>Equivale a</span>
                            <span style={{ fontWeight: 700 }}>{money(receivedUsdInPen)}</span>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span>Total a cubrir</span>
                            <span>{money(total)}</span>
                          </Box>
                          <Divider sx={{ my: 0.5 }} />
                          {!usdError ? (
                            <>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
                                <span>Cambio (PEN)</span>
                                <span style={{ color: '#10B981' }}>{money(changeUsdInPen)}</span>
                              </Box>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#6B7280' }}>
                                <span>o en USD</span>
                                <span>US$ {changeUsd.toFixed(2)}</span>
                              </Box>
                            </>
                          ) : (
                            <Alert severity="warning" sx={{ py: 0 }}>
                              Faltan {money(total - receivedUsdInPen)} (USD insuficiente).
                            </Alert>
                          )}
                        </Stack>
                      )}
                    </>
                  ) : (
                    <Alert severity="error" sx={{ py: 0 }}>
                      No hay tipo de cambio cargado al abrir el turno. No se puede cobrar en USD.
                    </Alert>
                  )}
                </Box>
              )}

              {/* Pagos mixtos: agregar varias lineas que sumen el total */}
              <Box sx={{ mt: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <TextField
                    size="small" type="number" label="Monto" value={lineAmount}
                    onChange={(e) => setLineAmount(e.target.value)}
                    placeholder={`${remaining.toFixed(2)}`}
                    InputProps={{ startAdornment: <InputAdornment position="start">S/</InputAdornment> }}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    size="small" label="Ref. (op./voucher)" value={lineRef}
                    onChange={(e) => setLineRef(e.target.value)}
                    sx={{ flex: 1 }}
                  />
                  <Button
                    variant="outlined" startIcon={<AddIcon />}
                    onClick={addPaymentLine}
                    disabled={remaining <= 0.001}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    Split
                  </Button>
                </Stack>
                {method === 'credito' && (
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                    <TextField
                      select size="small" label="Cliente *"
                      value={lineCustomerId}
                      onChange={(e) =>
                        setLineCustomerId(e.target.value === '' ? '' : Number(e.target.value))
                      }
                      sx={{ flex: 1 }}
                      SelectProps={{ native: true }}
                    >
                      <option value="">Selecciona cliente</option>
                      {customers.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name} · {c.document}
                        </option>
                      ))}
                    </TextField>
                    <TextField
                      type="date" size="small" label="Fecha pago *"
                      value={lineDueDate}
                      onChange={(e) => setLineDueDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ flex: 1 }}
                    />
                  </Stack>
                )}
                {paymentLines.length > 0 && (
                  <>
                    <List dense sx={{ mt: 1, bgcolor: '#F9FAFB', borderRadius: 1 }}>
                      {paymentLines.map((l, i) => {
                        const label =
                          PAYMENT_METHODS.find((m) => m.key === l.method)?.label || l.method;
                        const secondary = l.method === 'credito'
                          ? `Credito a ${l.customerName ?? '?'} · vence ${l.dueDate ?? '?'}`
                          : l.reference || undefined;
                        return (
                          <ListItem
                            key={i}
                            dense
                            secondaryAction={
                              <Button
                                size="small" color="error"
                                onClick={() => removePaymentLine(i)}
                                startIcon={<CloseSmallIcon />}
                              >
                                Quitar
                              </Button>
                            }
                          >
                            <ListItemText
                              primary={`${label}  ${money(l.amount)}`}
                              secondary={secondary}
                            />
                          </ListItem>
                        );
                      })}
                    </List>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        mt: 0.5,
                        fontWeight: 700,
                        color: mixtoListo ? '#10B981' : remaining < 0 ? '#DC2626' : '#D97706',
                      }}
                    >
                      <span>Cobrado / Total</span>
                      <span>{money(paidSum)} / {money(total)}</span>
                    </Box>
                    {!mixtoListo && remaining > 0 && (
                      <Alert severity="info" sx={{ mt: 0.5, py: 0 }}>
                        Falta cobrar {money(remaining)}.
                      </Alert>
                    )}
                  </>
                )}
              </Box>

              <Divider sx={{ my: 1.5 }} />
              <Typography variant="caption" sx={{ color: '#6B7280' }}>Tipo de comprobante</Typography>
              <ToggleButtonGroup
                value={comprobanteType} exclusive fullWidth size="small"
                onChange={(_e, v) => v && setComprobanteType(v)}
                sx={{ mt: 0.5 }}
              >
                <ToggleButton value="auto">Auto</ToggleButton>
                <ToggleButton value="boleta">Boleta</ToggleButton>
                <ToggleButton value="factura">Factura</ToggleButton>
                <ToggleButton value="nota_venta">Nota Venta</ToggleButton>
                <ToggleButton value="doc_interno">Interno</ToggleButton>
              </ToggleButtonGroup>
              {(comprobanteType === 'nota_venta' || comprobanteType === 'doc_interno') && (
                <Alert severity="warning" sx={{ mt: 1, py: 0 }}>
                  Comprobante INTERNO — no se enviara a SUNAT. El inventario se
                  descontara igual.
                </Alert>
              )}
              {/* RF-05: agrupar items en una sola linea "POR CONSUMO" para SUNAT */}
              {(comprobanteType === 'auto' ||
                comprobanteType === 'boleta' ||
                comprobanteType === 'factura') && (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <Checkbox
                    size="small"
                    checked={porConsumo}
                    onChange={(e) => setPorConsumo(e.target.checked)}
                  />
                  <Typography variant="body2">
                    Facturar como <b>"Por Consumo"</b> (1 sola linea en el XML SUNAT)
                  </Typography>
                </Box>
              )}
              <Typography variant="caption" sx={{ color: '#6B7280', display: 'block', mt: 1.5 }}>
                Datos del cliente (opcional)
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <TextField
                  select size="small" label="Doc" value={docType}
                  onChange={(e) => { setDocType(e.target.value); setDocNumber(''); setLookupMsg(null); }}
                  sx={{ minWidth: 110 }} SelectProps={{ native: true }}
                >
                  {docTypes.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
                </TextField>
                <TextField
                  size="small" label="Numero" value={docNumber}
                  onChange={(e) => { setDocNumber(e.target.value); setLookupMsg(null); }}
                  fullWidth disabled={!docType} inputProps={{ maxLength: maxLen }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (docType === 'DNI' || docType === 'RUC')) {
                      e.preventDefault();
                      buscarDocumento();
                    }
                  }}
                />
                <Button
                  variant="outlined" size="small"
                  startIcon={<SearchIcon />}
                  disabled={lookingUp || (docType !== 'DNI' && docType !== 'RUC')}
                  onClick={buscarDocumento}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {lookingUp ? '...' : 'Buscar'}
                </Button>
              </Stack>
              {lookupMsg && (
                <Alert
                  severity={lookupMsg.kind === 'ok' ? 'success' : lookupMsg.kind === 'error' ? 'warning' : 'info'}
                  sx={{ mt: 1, py: 0 }}
                >
                  {lookupMsg.text}
                </Alert>
              )}
              <TextField size="small" label="Nombre / Razon social" value={customerName} onChange={(e) => setCustomerName(e.target.value)} fullWidth sx={{ mt: 1 }} />
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={() => setSelected(null)} disabled={paying}>Cancelar</Button>
              <Button
                variant="outlined" color="warning" startIcon={<SplitIcon />}
                disabled={paying || (selected.items || []).length < 2}
                onClick={openSplitDialog}
              >
                Dividir
              </Button>
              <Button
                variant="contained" color="success" startIcon={<PrintIcon />}
                disabled={paying || (paymentLines.length > 0 && !mixtoListo)}
                onClick={cobrar}
              >
                {paying
                  ? 'Cobrando...'
                  : paymentLines.length > 0
                    ? mixtoListo
                      ? `Confirmar mixto ${money(paidSum)}`
                      : `Faltan ${money(remaining)}`
                    : `Confirmar pago ${money(selected.total)}`}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Dialogo movimiento */}
      <Dialog open={movOpen} onClose={() => setMovOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Movimiento de caja</DialogTitle>
        <DialogContent dividers>
          <ToggleButtonGroup value={movType} exclusive fullWidth size="small" onChange={(_e, v) => v && setMovType(v)} sx={{ mb: 2 }}>
            <ToggleButton value="ingreso" color="success">Ingreso</ToggleButton>
            <ToggleButton value="egreso" color="error">Egreso</ToggleButton>
          </ToggleButtonGroup>
          <TextField fullWidth size="small" label="Monto" type="number" value={movAmount}
            onChange={(e) => setMovAmount(e.target.value)} sx={{ mb: 2 }}
            InputProps={{ startAdornment: <InputAdornment position="start">S/</InputAdornment> }} />
          <TextField fullWidth size="small" label="Concepto" value={movConcept}
            onChange={(e) => setMovConcept(e.target.value)} placeholder="Ej: compra de hielo, retiro, propina" />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setMovOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={registrarMovimiento}>Registrar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialogo gasto diario */}
      <Dialog open={expenseOpen} onClose={() => !savingExpense && setExpenseOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Registrar gasto</DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2, py: 0 }}>
            El gasto se descuenta del efectivo esperado de esta caja.
          </Alert>
          <TextField
            fullWidth size="small" label="Monto" type="number" value={expenseAmount}
            onChange={(e) => setExpenseAmount(e.target.value)} sx={{ mb: 2 }}
            InputProps={{ startAdornment: <InputAdornment position="start">S/</InputAdornment> }}
            autoFocus
          />
          <TextField
            fullWidth size="small" label="Concepto" value={expenseConcept}
            onChange={(e) => setExpenseConcept(e.target.value)}
            placeholder="Ej: compra de hielo, verduras, gas"
            inputProps={{ maxLength: 255 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setExpenseOpen(false)} disabled={savingExpense}>Cancelar</Button>
          <Button
            variant="contained" color="error" startIcon={<ExpenseIcon />}
            disabled={savingExpense || !expenseConcept.trim() || !(parseFloat(expenseAmount) > 0)}
            onClick={registrarGasto}
          >
            {savingExpense ? 'Registrando...' : 'Registrar gasto'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialogo cierre */}
      <Dialog open={closeOpen} onClose={() => !closing && setCloseOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Cerrar turno (arqueo)</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={0.5} sx={{ mb: 2 }}>
            <Row l="Fondo inicial PEN" v={money(s.openingAmount)} />
            <Row l="Fondo inicial USD" v={dollars(s.openingAmountUsd)} />
            <Divider sx={{ my: 0.5 }} />
            <Row l="Ventas efectivo PEN" v={money(sales.efectivo)} />
            <Row l="Ventas efectivo USD" v={dollars(sales.efectivoUsd)} />
            <Row l="Ventas Visa" v={money(sales.visa)} />
            <Row l="Ventas Mastercard" v={money(sales.mastercard)} />
            <Row l="Ventas Yape" v={money(sales.yape)} />
            <Row l="Ventas Plin" v={money(sales.plin)} />
            <Row l="Ventas Transferencia" v={money(sales.transferencia)} />
            <Row l="Ventas Credito" v={money(sales.credito)} />
            <Divider sx={{ my: 0.5 }} />
            <Row l="Ingresos efectivo" v={money(cash.ingresos)} />
            <Row l="Gastos" v={money(expensesTotal)} />
            {expenses.length > 0 && (
              <Box sx={{ pl: 1.5, borderLeft: '3px solid #FCA5A5', my: 0.5 }}>
                {expenses.map((e) => (
                  <Box
                    key={e.id}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 1,
                      fontSize: 13,
                      color: '#6B7280',
                    }}
                  >
                    <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {e.concept}
                    </Box>
                    <Box sx={{ whiteSpace: 'nowrap' }}>{money(e.amount)}</Box>
                  </Box>
                ))}
              </Box>
            )}
            <Divider sx={{ my: 0.5 }} />
            <Row l="Saldo final esperado PEN" v={money(cash.expectedCash)} bold />
            <Row l="Saldo final esperado USD" v={dollars(cash.expectedCashUsd)} bold />
          </Stack>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <TextField fullWidth label="Efectivo contado PEN" type="number" value={counted}
              onChange={(e) => setCounted(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start">S/</InputAdornment> }} />
            <Button variant="outlined" startIcon={<CalcIcon />} onClick={() => setCalcOpen(true)} sx={{ mt: 0.5, whiteSpace: 'nowrap' }}>
              Contar
            </Button>
          </Box>
          <TextField fullWidth label="Efectivo contado USD" type="number" value={countedUsd}
            onChange={(e) => setCountedUsd(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start">US$</InputAdornment> }}
            sx={{ mt: 1.5 }} />

          {counted !== '' && !isNaN(countedNum) && (
            <Alert
              severity={!hasDescuadre ? 'success' : diff < 0 ? 'error' : 'info'}
              sx={{ mt: 1.5 }}
            >
              {!hasDescuadre
                ? 'Caja cuadrada en PEN'
                : diff < 0
                  ? `Faltante PEN: ${money(diff)}`
                  : `Sobrante PEN: +${money(diff)}`}
            </Alert>
          )}
          {countedUsd !== '' && !isNaN(parseFloat(countedUsd)) && (() => {
            const expectedUsd = Number(cash.expectedCashUsd ?? 0);
            const diffUsd = Math.round((parseFloat(countedUsd) - expectedUsd) * 100) / 100;
            const hasUsdDesc = Math.abs(diffUsd) >= 0.01;
            return (
              <Alert severity={!hasUsdDesc ? 'success' : diffUsd < 0 ? 'error' : 'info'} sx={{ mt: 1 }}>
                {!hasUsdDesc
                  ? 'Caja cuadrada en USD'
                  : diffUsd < 0
                    ? `Faltante USD: ${dollars(diffUsd)}`
                    : `Sobrante USD: +${dollars(diffUsd)}`}
              </Alert>
            );
          })()}
          <TextField
            fullWidth size="small" sx={{ mt: 1.5 }} multiline rows={2}
            label={notesRequired ? 'Observaciones (obligatorio por descuadre)' : 'Observaciones (opcional)'}
            value={closeNotes}
            onChange={(e) => setCloseNotes(e.target.value)}
            required={notesRequired}
            error={notesRequired && !closeNotes.trim()}
            helperText={notesRequired && !closeNotes.trim() ? 'Explica el faltante/sobrante para poder cerrar.' : ' '}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setCloseOpen(false)} disabled={closing}>Cancelar</Button>
          <Button variant="contained" color="error" startIcon={<CloseIcon />} disabled={closing || closeBlocked} onClick={cerrarCaja}>
            {closing ? 'Cerrando...' : 'Cerrar e imprimir'}
          </Button>
        </DialogActions>
      </Dialog>

      <DenominationCalculator open={calcOpen} onClose={() => setCalcOpen(false)} onApply={(t) => setCounted(String(t))} />

      {/* Dialogo de traspaso de item (RF-03) — requiere autorizacion Admin */}
      <Dialog open={transferOpen} onClose={() => !transferring && setTransferOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          Trasladar item a otra mesa
          {transferItem && (
            <Typography variant="body2" sx={{ color: '#6B7280' }}>
              {transferItem.quantity}x {transferItem.recipe?.name ?? ''} — {money(transferItem.subtotal)}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 1, py: 0 }}>
            Accion auditada. Se solicita contrasena de un usuario con rol Admin.
          </Alert>
          <TextField
            select fullWidth size="small" label="Mesa destino"
            value={transferTargetTable}
            onChange={(e) => setTransferTargetTable(e.target.value === '' ? '' : Number(e.target.value))}
            sx={{ mt: 1.5 }}
            SelectProps={{ native: true }}
          >
            <option value="">Selecciona una mesa</option>
            {allTables
              .filter((t) => t.id !== selected?.tableId)
              .map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} ({t.status === 'ocupada' ? 'ocupada' : 'libre'}) — Zona: {t.zone || 'salon'}
                </option>
              ))}
          </TextField>
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="caption" sx={{ color: '#6B7280' }}>Autorizacion Admin</Typography>
          <TextField
            fullWidth size="small" label="Email Admin" value={transferAdminEmail}
            onChange={(e) => setTransferAdminEmail(e.target.value)}
            placeholder="admin@resta.com" sx={{ mt: 1 }}
          />
          <TextField
            fullWidth size="small" type="password" label="Contrasena Admin"
            value={transferAdminPassword}
            onChange={(e) => setTransferAdminPassword(e.target.value)}
            sx={{ mt: 1 }}
          />
          <TextField
            fullWidth size="small" label="Motivo (opcional)" value={transferReason}
            onChange={(e) => setTransferReason(e.target.value)}
            placeholder="Cliente cambio de mesa, error de toma..."
            sx={{ mt: 1 }} multiline rows={2}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setTransferOpen(false)} disabled={transferring}>Cancelar</Button>
          <Button
            variant="contained" color="warning" startIcon={<SwapIcon />}
            disabled={transferring || !transferTargetTable || !transferAdminEmail || !transferAdminPassword}
            onClick={confirmTransfer}
          >
            {transferring ? 'Trasladando...' : 'Confirmar traslado'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialogo de split (por items o partes iguales) */}
      <Dialog open={splitOpen} onClose={() => !splitting && setSplitOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Dividir cuenta
          {selected && (
            <Typography variant="body2" sx={{ color: '#6B7280' }}>
              {selected.code} — {selected.table?.name || `Mesa ${selected.tableId}`} — Total {money(selected.total)}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          <ToggleButtonGroup
            value={splitMode} exclusive fullWidth size="small"
            onChange={(_e, v) => v && setSplitMode(v)}
            sx={{ mb: 2 }}
          >
            <ToggleButton value="items">Por items</ToggleButton>
            <ToggleButton value="equal">Partes iguales</ToggleButton>
          </ToggleButtonGroup>

          {splitMode === 'items' && (
            <>
              <Alert severity="info" sx={{ mb: 1, py: 0 }}>
                Marca los items que pasan al nuevo pedido. El resto queda en la cuenta original.
              </Alert>
              <List dense>
                {(selected?.items || [])
                  .filter((it: any) => it.status !== 'cancelado')
                  .map((it: any) => (
                    <ListItem
                      key={it.id}
                      dense
                      onClick={() => toggleSplitItem(it.id)}
                      sx={{ cursor: 'pointer' }}
                      secondaryAction={<Typography>{money(it.subtotal)}</Typography>}
                    >
                      <Checkbox
                        edge="start"
                        checked={selectedItemIds.has(it.id)}
                        tabIndex={-1}
                        disableRipple
                      />
                      <ListItemText
                        primary={`${it.quantity}x ${it.recipe?.name ?? ''}`}
                        secondary={it.notes || undefined}
                      />
                    </ListItem>
                  ))}
              </List>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
                <span>A separar ({selectedItemIds.size} items)</span>
                <span>{money(splitItemsTotal)}</span>
              </Box>
            </>
          )}

          {splitMode === 'equal' && (
            <>
              <Alert severity="warning" sx={{ mb: 2, py: 0 }}>
                Se crearan N pedidos hijos sin items y el original quedara cancelado.
                Cada hijo se cobra por separado (cliente y boleta propios). El inventario
                se descuenta una sola vez del pedido original.
              </Alert>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <Typography>Dividir en</Typography>
                <ToggleButtonGroup
                  value={splitParts} exclusive size="small"
                  onChange={(_e, v) => v && setSplitParts(Number(v))}
                >
                  {[2, 3, 4, 5, 6].map((n) => (
                    <ToggleButton key={n} value={n}>{n}</ToggleButton>
                  ))}
                </ToggleButtonGroup>
                <Typography>partes</Typography>
              </Stack>
              {selected && (
                <Paper sx={{ p: 1.5, bgcolor: '#F9FAFB' }}>
                  <Typography variant="caption" sx={{ color: '#6B7280' }}>Previsualizacion</Typography>
                  <List dense>
                    {Array.from({ length: splitParts }).map((_, i) => {
                      const t = Number(selected.total);
                      const por = Math.round((t / splitParts) * 100) / 100;
                      const ultima = Math.round((t - por * (splitParts - 1)) * 100) / 100;
                      const amt = i === splitParts - 1 ? ultima : por;
                      return (
                        <ListItem key={i} dense
                          secondaryAction={<Typography fontWeight={700}>{money(amt)}</Typography>}
                        >
                          <ListItemText primary={`Parte ${i + 1} / ${splitParts}`} />
                        </ListItem>
                      );
                    })}
                  </List>
                </Paper>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setSplitOpen(false)} disabled={splitting}>Cancelar</Button>
          {splitMode === 'items' ? (
            <Button
              variant="contained" color="warning" startIcon={<SplitIcon />}
              disabled={splitting || selectedItemIds.size === 0}
              onClick={confirmSplit}
            >
              {splitting ? 'Dividiendo...' : `Crear nuevo pedido (${money(splitItemsTotal)})`}
            </Button>
          ) : (
            <Button
              variant="contained" color="warning" startIcon={<SplitIcon />}
              disabled={splitting}
              onClick={confirmSplitEqual}
            >
              {splitting ? 'Dividiendo...' : `Crear ${splitParts} pedidos iguales`}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const Row: React.FC<{ l: string; v: string; bold?: boolean }> = ({ l, v, bold }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', fontWeight: bold ? 700 : 400, fontSize: bold ? 18 : 14 }}>
    <span>{l}</span><span>{v}</span>
  </Box>
);

export default Caja;
